import { createContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface Profile {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  display_name: string | null;
  role: 'student' | 'professor' | 'judge' | 'recruiter';
  school: string | null;
  bio: string | null;
  avatar_url: string | null;
  resume_url: string | null;
  linkedin_url: string | null;
  leaderboard_opt_in: boolean;
  updated_at: string;
  onboarding_goals: string[] | null;
  onboarding_weaknesses: string[] | null;
  daily_practice_minutes: number | null;
  weekly_practice_days: number | null;
  onboarding_completed_at: string | null;
  email_reminders_enabled: boolean | null;
  reminder_time: string | null;
  reminder_days: string[] | null;
  weekly_summary_enabled: boolean | null;
  milestone_emails_enabled: boolean | null;
  graduation_date: string | null;
  major: string | null;
  additional_majors: string[] | null;
  minors_certificates: string[] | null;
  target_roles: string[] | null;
  work_authorization: string | null;
  relocation_willing: boolean | null;
  profile_completed_at: string | null;
  student_email: string | null;
  student_verified_at: string | null;
  user_type: 'student' | 'professional' | 'professor' | 'recruiter' | 'manager' | 'sales_rep' | null;
  professional_role: string | null;
  years_experience: string | null;
  industry: string | null;
  deal_size: string | null;
  sales_type: string | null;
  sales_cycle: string | null;
  biggest_challenge: string | null;
  company_name: string | null;
  products_services: string | null;
  terms_accepted_at: string | null;
  featured_speedsell_url: string | null;
}

export interface ProfileContextType {
  profile: Profile | null;
  loading: boolean;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  uploadAvatar: (file: File) => Promise<{ url: string | null; error: Error | null }>;
  deleteAvatar: () => Promise<{ error: Error | null }>;
  uploadResume: (file: File) => Promise<{ url: string | null; error: Error | null }>;
  deleteResume: () => Promise<{ error: Error | null }>;
  refetch: (isInitial?: boolean) => Promise<void>;
}

export const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Tracks which user id we've already loaded a profile for, so background
  // refetches (profile updates, auth token refresh) don't flip `loading` and
  // unmount consumers like ProtectedRoute mid-session.
  const lastFetchedUserIdRef = useRef<string | null>(null);

  const fetchProfile = useCallback(async (isInitial = false) => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      lastFetchedUserIdRef.current = null;
      return;
    }

    // Only show the blocking loading state on the first fetch for this user.
    // Background refetches for the same user must NOT toggle loading, or
    // ProtectedRoute would unmount the active page (killing a live session).
    const isNewUser = lastFetchedUserIdRef.current !== user.id;
    if (isInitial || isNewUser) {
      setLoading(true);
    }
    lastFetchedUserIdRef.current = user.id;

    const deriveRole = (): Profile['role'] => {
      const metaRole = (user.user_metadata as any)?.invite_role;
      if (metaRole === 'professor' || metaRole === 'judge' || metaRole === 'recruiter' || metaRole === 'student') {
        return metaRole;
      }
      return 'student';
    };

    const deriveName = (): string | null => {
      const metaName = (user.user_metadata as any)?.full_name ?? (user.user_metadata as any)?.name;
      return typeof metaName === 'string' && metaName.trim() ? metaName.trim() : null;
    };

    try {
      const [profileResult, roleResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle(),
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
      ]);

      if (profileResult.error) throw profileResult.error;

      let userRole: Profile['role'] = 'student';
      const roles: string[] = Array.isArray(roleResult.data)
        ? roleResult.data.map((r: any) => r.role).filter(Boolean)
        : [];
      // Prefer privileged roles over plain 'user'
      const priority = ['admin', 'recruiter', 'professor', 'judge', 'student'];
      const picked = priority.find(r => roles.includes(r));
      if (picked) {
        userRole = picked as Profile['role'];
      } else if (roles.includes('user')) {
        userRole = 'student';
      }

      if (!profileResult.data) {
        const role = userRole !== 'student' ? userRole : deriveRole();
        const name = deriveName();
        
        const pendingTermsAccepted = localStorage.getItem('pending_terms_accepted_at');

        // Derive user_type from metadata (set during sign-up)
        const deriveUserType = (): 'student' | 'professional' | null => {
          const metaUserType = (user.user_metadata as any)?.user_type;
          if (metaUserType === 'student' || metaUserType === 'professional') {
            return metaUserType;
          }
          return null;
        };

        const { data: inserted, error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            full_name: name,
            display_name: name,
            role,
            user_type: deriveUserType(),
            leaderboard_opt_in: true,
            updated_at: new Date().toISOString(),
            terms_accepted_at: pendingTermsAccepted || new Date().toISOString(),
          })
          .select('*')
          .single();

        if (!insertError && pendingTermsAccepted) {
          localStorage.removeItem('pending_terms_accepted_at');
        }

        if (insertError) {
          const { data: existing } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle();

          if (existing) {
            setProfile({ ...existing, role: userRole } as Profile);
          } else {
            setProfile(null);
          }
        } else {
          setProfile({ ...inserted, role: userRole } as Profile);
        }

        return;
      }

      // Backfill user_type from auth metadata if missing on existing profile rows
      let profileData: any = profileResult.data;
      if (!profileData.user_type) {
        const metaUserType = (user.user_metadata as any)?.user_type;
        if (metaUserType === 'student' || metaUserType === 'professional') {
          const { error: backfillError } = await supabase
            .from('profiles')
            .update({ user_type: metaUserType, updated_at: new Date().toISOString() })
            .eq('id', user.id);
          if (!backfillError) {
            profileData = { ...profileData, user_type: metaUserType };
          }
        }
      }

      setProfile({ ...profileData, role: userRole } as Profile);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [user, hasFetched]);

  // Fetch only when the actual user changes (login/logout), not on every
  // token-refresh re-render. fetchProfile is intentionally omitted from deps.
  useEffect(() => {
    fetchProfile(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;
      
      await fetchProfile();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const uploadAvatar = async (file: File): Promise<{ url: string | null; error: Error | null }> => {
    if (!user) return { url: null, error: new Error('Not authenticated') };

    if (file.size > 2 * 1024 * 1024) {
      return { url: null, error: new Error('File must be less than 2MB') };
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { url: null, error: new Error('File must be JPG, PNG, or WebP') };
    }

    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `avatar_${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { data: existingFiles } = await supabase.storage
        .from('avatars')
        .list(user.id);
      
      if (existingFiles && existingFiles.length > 0) {
        const filePaths = existingFiles.map(f => `${user.id}/${f.name}`);
        await supabase.storage.from('avatars').remove(filePaths);
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const avatarUrl = urlData.publicUrl;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) throw updateError;

      await fetchProfile();
      return { url: avatarUrl, error: null };
    } catch (error) {
      return { url: null, error: error as Error };
    }
  };

  const deleteAvatar = async (): Promise<{ error: Error | null }> => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { data: files } = await supabase.storage
        .from('avatars')
        .list(user.id);

      if (files && files.length > 0) {
        const filePaths = files.map(f => `${user.id}/${f.name}`);
        await supabase.storage.from('avatars').remove(filePaths);
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) throw updateError;

      await fetchProfile();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const uploadResume = async (file: File): Promise<{ url: string | null; error: Error | null }> => {
    if (!user) return { url: null, error: new Error('Not authenticated') };

    if (file.size > 5 * 1024 * 1024) {
      return { url: null, error: new Error('File must be less than 5MB') };
    }

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      return { url: null, error: new Error('File must be PDF or Word document') };
    }

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/resume.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Store the raw storage path instead of a signed URL to avoid expiration
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ resume_url: filePath, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) throw updateError;

      await fetchProfile();
      return { url: filePath, error: null };
    } catch (error) {
      return { url: null, error: error as Error };
    }
  };

  const deleteResume = async (): Promise<{ error: Error | null }> => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { data: files } = await supabase.storage
        .from('resumes')
        .list(user.id);

      if (files && files.length > 0) {
        const filePaths = files.map(f => `${user.id}/${f.name}`);
        await supabase.storage.from('resumes').remove(filePaths);
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ resume_url: null, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) throw updateError;

      await fetchProfile();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  return (
    <ProfileContext.Provider value={{ 
      profile, 
      loading, 
      updateProfile, 
      uploadAvatar, 
      deleteAvatar, 
      uploadResume, 
      deleteResume, 
      refetch: fetchProfile 
    }}>
      {children}
    </ProfileContext.Provider>
  );
}
